import { useMemo } from 'react';
import { cn } from './cn';

/**
 * The four-point star is the product's signature mark — it is the "(x)" in
 * e(x)amine. It appears as the sparkle reaction, the loading state, the
 * celebration, and the logo glyph, so it lives here once.
 */
export function SparkleGlyph({ className, ...rest }: React.SVGProps<SVGSVGElement>) {
  return (
    <svg viewBox="0 0 24 24" className={cn('h-5 w-5', className)} aria-hidden {...rest}>
      <path
        d="M12 0.8c.5 5.2 2.4 8.2 6.6 9.4 1.5.4 2.9.6 4.6.8-5.6.6-8.7 2.5-9.9 7-.4 1.4-.9 3.1-1.3 5.2-.4-2.1-.9-3.8-1.3-5.2-1.2-4.5-4.3-6.4-9.9-7 1.7-.2 3.1-.4 4.6-.8C9.6 9 11.5 6 12 .8Z"
        fill="currentColor"
      />
    </svg>
  );
}

type FieldProps = {
  count?: number;
  className?: string;
  /** Seed keeps the layout stable across re-renders. */
  seed?: number;
};

/** A drift of background sparkles. Purely decorative; hidden from AT. */
export function SparkleField({ count = 14, className, seed = 7 }: FieldProps) {
  // Bedtime reduces optional effects. Read from the DOM rather than threaded
  // through props so no screen needs a bedtime branch.
  const quiet =
    typeof document !== 'undefined' && document.documentElement.dataset.mood === 'bedtime';
  const stars = useMemo(() => {
    // Deterministic pseudo-random so the sky doesn't reshuffle on every render.
    let s = seed * 9301 + 49297;
    const rnd = () => ((s = (s * 9301 + 49297) % 233280) / 233280);
    return Array.from({ length: count }, () => ({
      left: rnd() * 100,
      top: rnd() * 100,
      size: 6 + rnd() * 16,
      delay: rnd() * 3,
      dur: 2.2 + rnd() * 2.6,
      hue: rnd(),
    }));
  }, [count, seed]);

  if (quiet) return null;

  return (
    <div aria-hidden className={cn('pointer-events-none absolute inset-0 overflow-hidden', className)}>
      {stars.map((s, i) => (
        <SparkleGlyph
          key={i}
          className={cn(
            'absolute animate-twinkle',
            s.hue > 0.66 ? 'text-magic' : s.hue > 0.33 ? 'text-sunny' : 'text-accent',
          )}
          style={{
            left: `${s.left}%`,
            top: `${s.top}%`,
            width: s.size,
            height: s.size,
            animationDelay: `${s.delay}s`,
            animationDuration: `${s.dur}s`,
          }}
        />
      ))}
    </div>
  );
}

/**
 * A burst used for real moments only: a picture saved, a sparkle sent
 * (spec §4.1 — keep sparkle effects focused on selected moments).
 */
export function SparkleBurst({ className }: { className?: string }) {
  const rays = Array.from({ length: 8 }, (_, i) => (i * 360) / 8);
  return (
    <div aria-hidden className={cn('pointer-events-none absolute inset-0 grid place-items-center', className)}>
      {rays.map((deg, i) => (
        <SparkleGlyph
          key={deg}
          className="absolute h-4 w-4 animate-pop-in text-magic"
          style={{
            transform: `rotate(${deg}deg) translateY(-54px)`,
            animationDelay: `${i * 45}ms`,
          }}
        />
      ))}
    </div>
  );
}
